import React, { useEffect, useMemo, useState } from "react";
import { useTable, useSortBy, usePagination, useRowSelect } from "react-table";
import "tailwindcss/tailwind.css";
import { Empty } from "antd";
import "../../styles.css";
import Vectorsorting from "../../assets/Vectorsorting.svg";
import ShimmerLoader from "../../loading";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CustomPagination from "../../components/CustomWidgets/CustomPagination";
import { useSelector } from "react-redux";
import { routes } from "../../Routes";

const VendorPaymentTable = ({
  handlePaymentClick,
  showDrawer,
  additionalData,
  pagination,
}) => {
  const { t } = useTranslation();
  const [flag, setflag] = useState(false);
  const navigate = useNavigate();

  const allStores = useSelector(
    (state) => state?.storeReducer?.selectedStore?.is_select_all_store
  );

  const openVendor = (id) => {
    navigate(`/vendorDetails?id=${id}`, {
      state: { from: "vendor_payment" },
    });
  };

  const handleRowClick = (row) => {
    if (row.status === "draft") {
      navigate(routes.purchase.addBill, {
        state: { orderData: row._raw },
      });
    } else {
      navigate(routes.purchase.purchaseOrder, {
        state: { orderData: row._raw },
      });
    }
  };

  const data = useMemo(() => additionalData || [], [additionalData]);

  useEffect(() => {
    if (additionalData?.length === 0 && !flag) {
      const timer = setTimeout(() => setflag(true), 3000);
      return () => clearTimeout(timer);
    }
  }, [additionalData, flag]);

  // px widths — table scrolls horizontally instead of squishing
  const col = {
    orderNo:      120,
    vendor:       140,
    billDate:     110,
    createDate:   110,
    dueDate:      100,
    billNo:       110,
    paymentTerm:  130,
    quantity:      80,
    invoiceValue: 120,
    paidAmount:   120,
    dueAmount:    110,
    status:       140,
    storeName:    120,
    actions:      220,
  };

  const columns = useMemo(
    () => [
      {
        Header: t("Order No."),
        accessor: "order_no",
        width: col.orderNo,
        Cell: ({ row }) => (
          <div className="truncate" title={row.original.order_no}>
            {row.original.order_no || t("Draft")}
          </div>
        ),
      },
      {
        Header: t("Vendor"),
        accessor: "supplier_name",
        width: col.vendor,
        Cell: ({ row }) => (
          <div
            className="truncate cursor-pointer hover:underline"
            onClick={(e) => { e.stopPropagation(); openVendor(row.original.supplier_id); }}
            title={row.original.supplier_name}
          >
            {row.original.supplier_name}
          </div>
        ),
      },
      {
        Header: t("Bill Date"),
        accessor: "order_date",
        width: col.billDate,
        Cell: ({ row }) => (
          <div>{row.original.order_date ? row.original.order_date.split(" ")[0] : "-"}</div>
        ),
      },
      {
        Header: t("Create Date"),
        accessor: "create_date",
        width: col.createDate,
        Cell: ({ row }) => (
          <div>{row.original.create_date ? row.original.create_date.split(" ")[0] : "-"}</div>
        ),
      },
      {
        Header: t("Due Date"),
        accessor: "due_date",
        width: col.dueDate,
        Cell: ({ row }) => (
          <div>{row.original.due_date || "-"}</div>
        ),
      },
      {
        Header: t("Bill No."),
        accessor: "vendor_reference",
        width: col.billNo,
        Cell: ({ row }) => (
          <div className="truncate">{row.original.vendor_reference || "--"}</div>
        ),
      },
      {
        Header: t("Payment Term"),
        accessor: "payment_term",
        width: col.paymentTerm,
        Cell: ({ row }) => (
          <div className="truncate">{row.original.payment_term || "-"}</div>
        ),
      },
      {
        Header: t("Quantity"),
        accessor: "quantity",
        width: col.quantity,
        Cell: ({ row }) => <div>{row.original.quantity ?? "-"}</div>,
      },
      {
        Header: t("Invoice Value"),
        accessor: "invoice_value",
        width: col.invoiceValue,
        Cell: ({ row }) => (
          <div>{`EGP ${row.original.invoice_value?.toFixed(2)}`}</div>
        ),
      },
      {
        Header: t("Paid Amount"),
        accessor: "paid_amount",
        width: col.paidAmount,
        Cell: ({ row }) => (
          <div>{`EGP ${row.original.paid_amount?.toFixed(2)}`}</div>
        ),
      },
      {
        Header: t("Due Amount"),
        accessor: "due_amount",
        width: col.dueAmount,
        Cell: ({ row }) => (
          <div>{`EGP ${row.original.due_amount?.toFixed(2)}`}</div>
        ),
      },
      {
        Header: t("Status"),
        accessor: "status",
        width: col.status,
        Cell: ({ row }) => {
          const od = row.original;

          if (od.status === "cancel") {
            return (
              <span className="inline-flex justify-center font-normal text-xs rounded-xl bg-[#ffd1d1] text-[#ef3a3a] px-3 py-1 whitespace-nowrap">
                {t("Cancelled")}
              </span>
            );
          }
          if (od.invoice_value < 0 && od.status !== "draft") {
            return (
              <span className="inline-flex justify-center font-normal text-xs rounded-xl bg-[#fbded9] text-red-500 px-3 py-1 whitespace-nowrap">
                {t("Return")}
              </span>
            );
          }
          if (od.status === "draft") {
            return (
              <span className="inline-flex justify-center font-normal text-xs rounded-xl bg-gray-100 text-gray-500 px-3 py-1 whitespace-nowrap">
                {t("Draft Order")}
              </span>
            );
          }

          const isPartial = od.paid_amount > 0 && od.due_amount > 0 && od.invoice_value > 0;
          const isUnpaid  = od.paid_amount === 0 && od.invoice_value > 0;
          const isPaid    = od.paid_amount > 0 && od.due_amount === 0 && od.invoice_value > 0;

          const label = isPartial ? t("Partial Payment") : isUnpaid ? t("Unpaid") : isPaid ? t("Full Payment") : t("Unknown Status");
          const cls   = isPartial || isUnpaid ? "bg-[#FFEBC5] text-[#E8A700]" : isPaid ? "bg-[#D1FFDE] text-[#00B833]" : "bg-gray-100 text-gray-500";

          return (
            <span className={`inline-flex justify-center font-normal text-xs rounded-xl px-3 py-1 whitespace-nowrap ${cls}`}>
              {label}
            </span>
          );
        },
      },
      ...(allStores
        ? [{
            Header: t("Store Name"),
            accessor: "store_name",
            width: col.storeName,
            Cell: ({ row }) => (
              <div className="truncate" title={row.original.store_name}>
                {row.original.store_name}
              </div>
            ),
          }]
        : []),
      {
        Header: "",
        accessor: "bill_no",
        width: col.actions,
        Cell: ({ row }) => (
          <div
            className="flex gap-2 items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => showDrawer(row.original)}
              className="font-semibold w-36 h-8 text-xs border border-primaryBlue rounded-lg bg-white text-primaryBlue flex justify-center items-center gap-1 shrink-0"
            >
              {t("Payment History")}
            </button>
            {!(row.original.paid_amount > 0 && row.original.due_amount === 0) &&
              row.original.invoice_value >= 0 &&
              row.original.status !== "draft" &&
              row.original.status !== "cancel" && (
                <button
                  onClick={() => handlePaymentClick(row.original)}
                  className="font-semibold w-14 h-8 border rounded-lg bg-primaryBlue text-white text-xs flex justify-center items-center shrink-0"
                >
                  {t("Pay")}
                </button>
              )}
          </div>
        ),
      },
    ],
    [t, allStores]
  );

  const {
    headerGroups,
    prepareRow,
    page,
    nextPage,
    setPageSize,
    state: { pageSize },
  } = useTable(
    { columns, data, initialState: { pageSize: 10 } },
    useSortBy,
    usePagination,
    useRowSelect
  );

  const [pageIndex, setPageIndex] = useState(1);

  useEffect(() => {
    const pageDetails = {
      limit: pageSize,
      offset: pageSize * (pageIndex - 1) < 0 ? 0 : pageSize * (pageIndex - 1),
    };
    if (pageSize) pagination(pageDetails);
  }, [pageSize, pageIndex]);

  // total min-width so the table never squishes
  const totalWidth = Object.values(col).reduce((a, b) => a + b, 0)
    + (allStores ? col.storeName : 0);

  return (
    <div className="container flex flex-col mx-auto mt-4" style={{ maxWidth: "100%" }}>
      {additionalData?.length === 0 && flag === false ? (
        <div className="h-screen w-[90%] mt-2">
          <ShimmerLoader />
        </div>
      ) : (
        <>
          {/* Outer scroll wrapper — both header and body scroll together */}
          <div style={{ overflowX: "auto", width: "100%" }}>

            {/* Fixed header */}
            <table
              className="font-normal rounded-lg"
              style={{ minWidth: totalWidth, width: "100%", tableLayout: "fixed", borderCollapse: "separate", borderSpacing: 0 }}
            >
              <thead>
                {headerGroups.map((headerGroup) => {
                  const { key, ...props } = headerGroup.getHeaderGroupProps();
                  return (
                    <tr key={key} {...props} className="bg-[#78CFEC66] text-sm font-semibold">
                      {headerGroup.headers.map((column) => {
                        const { key: ck, ...cp } = column.getHeaderProps(column.getSortByToggleProps());
                        return (
                          <th
                            key={ck}
                            {...cp}
                            className="py-3 px-3 whitespace-nowrap text-left"
                            style={{ minWidth: column.width, width: column.width }}
                          >
                            <div className="flex flex-row items-center text-sm leading-tight">
                              {column.render("Header")}
                              {column.canSort && (
                                <span className="ms-1">
                                  {column.isSorted ? (
                                    column.isSortedDesc
                                      ? <img src={Vectorsorting} style={{ minHeight: 10, minWidth: 10 }} alt="desc" />
                                      : <img src={Vectorsorting} className="rotate-180" alt="asc" />
                                  ) : (
                                    <img src={Vectorsorting} style={{ minHeight: 10, minWidth: 10, opacity: 0.3 }} alt="sort" />
                                  )}
                                </span>
                              )}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  );
                })}
              </thead>

              {/* Body inside same table so columns align perfectly */}
              <tbody className="bg-white">
                {page.map((row) => {
                  prepareRow(row);
                  return (
                    <React.Fragment key={row.id}>
                      <tr
                        {...row.getRowProps()}
                        className="hover:bg-[#F0F8FF] cursor-pointer transition duration-200 border-b border-gray-200"
                        onClick={() => handleRowClick(row.original)}
                      >
                        {row.cells.map((cell, index) => (
                          <td
                            key={index}
                            {...cell.getCellProps()}
                            className="px-3 py-3 text-[#272728] text-sm font-normal"
                            style={{ minWidth: cell.column.width, width: cell.column.width }}
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    </React.Fragment>
                  );
                })}
                {page.length === 0 && (
                  <tr>
                    <td colSpan="100%">
                      <Empty className="mt-10" description={t("No data available")} />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination outside scroll wrapper */}
          <CustomPagination
            data={data?.length}
            pageIndex={pageIndex}
            pageCount={page?.length}
            canPreviousPage={pageIndex !== 1}
            canNextPage={data?.length === pageSize}
            gotoPage={(p) => nextPage(p)}
            previousPage={() => setPageIndex(pageIndex - 1)}
            nextPage={() => setPageIndex(pageIndex + 1)}
            pageSize={pageSize}
            setPageSize={(s) => setPageSize(s)}
          />
        </>
      )}
    </div>
  );
};

export default VendorPaymentTable;